import React, { useEffect, useState } from 'react';
import {
  deleteCampaignForCreator,
  FEEDBACK_OPTIONS,
  getAnalyticsForCampaign,
  getCampaignAnalyticsTotals,
  getCampaignsForCreator,
  getFeedbackForCampaign,
  getFeedbackTotals,
  getGrindsForCampaign
} from '../services/campaignStore';
import { getCompletionTokenRate, getFeedbackQuestion, getInterestLabels } from '../services/discoveryConfig';
import { areCloudGrindsEnabled, getCloudGrindCount } from '../services/cloudGrindStore';

function CampaignFeedback({ userProfile, onNavigateToPostLink, onEditCampaign }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const objectUrls = [];
    let cancelled = false;

    const loadCampaigns = async () => {
      setLoading(true);
      const creatorId = userProfile?.uid || userProfile?.email || 'local-creator';
      const localCampaigns = await getCampaignsForCreator(creatorId).catch(() => []);
      const campaignsWithFeedback = await Promise.all(localCampaigns.map(async (campaign) => {
        const feedback = await getFeedbackForCampaign(campaign.id).catch(() => []);
        const analytics = await getAnalyticsForCampaign(campaign.id).catch(() => []);
        const grinds = await getGrindsForCampaign(campaign.id).catch(() => []);
        const cloudGrindCount = await getSharedGrindCount(campaign, grinds.length);
        const visualUrl = campaign.visual?.blob ? URL.createObjectURL(campaign.visual.blob) : '';
        if (visualUrl) objectUrls.push(visualUrl);

        return {
          ...campaign,
          feedback,
          analytics,
          grinds,
          analyticsTotals: {
            ...getCampaignAnalyticsTotals(analytics, feedback, grinds),
            grinds: cloudGrindCount
          },
          totals: getFeedbackTotals(feedback),
          visualUrl
        };
      }));

      if (!cancelled) {
        setCampaigns(campaignsWithFeedback);
        setLoading(false);
      }
    };

    loadCampaigns();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [userProfile, reloadKey]);

  const handleDeleteCampaign = async (campaign) => {
    const confirmed = window.confirm(`Delete "${campaign.title || 'Untitled Campaign'}" from this browser? This will also remove its local feedback and analytics records.`);
    if (!confirmed) return;

    try {
      const creatorId = userProfile?.uid || userProfile?.email || 'local-creator';
      await deleteCampaignForCreator(campaign.id, creatorId);
      setCampaigns((items) => items.filter((item) => item.id !== campaign.id));
      setNotice({ type: 'success', message: 'Local campaign deleted.' });
      setReloadKey((key) => key + 1);
    } catch (error) {
      setNotice({ type: 'error', message: `Campaign could not be deleted: ${error.message}` });
    }
  };

  if (loading) {
    return <div style={pageStyle}>Loading campaign feedback...</div>;
  }

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div>
          <h1 style={{ color: '#333', margin: '0 0 8px 0' }}>My Campaigns</h1>
          <p style={{ color: '#666', margin: 0 }}>
            Campaign Feedback is private and local for campaigns saved in this browser.
          </p>
        </div>
        <button type="button" onClick={onNavigateToPostLink} style={primaryButtonStyle}>
          Create Campaign
        </button>
      </header>

      <div style={localNoticeStyle}>
        Local-only Stage 4: this feedback is private to this browser. Cross-device and cross-user publishing will not work until the Firebase stage.
      </div>

      {notice && (
        <div role="status" aria-live="polite" style={notice.type === 'error' ? noticeErrorStyle : noticeSuccessStyle}>
          {notice.message}
        </div>
      )}

      {campaigns.length === 0 ? (
        <section style={emptyStyle}>
          <h2 style={{ color: '#333', marginTop: 0 }}>No local campaigns yet</h2>
          <p style={{ color: '#666' }}>Save a draft or publish a campaign to see feedback here.</p>
          <button type="button" onClick={onNavigateToPostLink} style={primaryButtonStyle}>
            Create Campaign
          </button>
        </section>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {campaigns.map((campaign) => (
            <section key={campaign.id} aria-labelledby={`campaign-${campaign.id}`} style={campaignCardStyle}>
              <div style={campaignHeaderStyle}>
                <div>
                  <div style={statusBadgeStyle(campaign.status)}>{campaign.status || 'Draft'}</div>
                  <h2 id={`campaign-${campaign.id}`} style={{ color: '#333', margin: '8px 0 4px 0' }}>
                    {campaign.title || 'Untitled Campaign'}
                  </h2>
                  <p style={{ color: '#666', margin: 0 }}>
                    {[campaign.platform, campaign.creatorType].filter(Boolean).join(' / ') || 'No category selected'}
                  </p>
                  <div style={campaignContextStyle}>
                    <span>
                      <strong>Audience:</strong> {formatAudience(campaign)}
                    </span>
                    <span>
                      <strong>Future completion rate:</strong> {getStoredCompletionRate(campaign)} tokens
                    </span>
                    <span>
                      <strong>Question:</strong> {getFeedbackQuestion(campaign.feedbackQuestion)}
                    </span>
                    <span>
                      <strong>Destination:</strong> {campaign.destination?.url ? `${campaign.destination.label} - ${campaign.destination.url}` : 'Not set for this campaign'}
                    </span>
                  </div>
                  <div style={campaignActionsStyle}>
                    <button type="button" onClick={() => onEditCampaign(campaign)} style={editButtonStyle}>
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDeleteCampaign(campaign)} style={deleteButtonStyle}>
                      Delete
                    </button>
                  </div>
                </div>
                {campaign.visualUrl ? (
                  <div style={thumbnailStyle}>
                    <img src={campaign.visualUrl} alt="" style={thumbnailImageStyle(campaign.visual?.crop)} />
                  </div>
                ) : (
                  <div style={thumbnailPlaceholderStyle}>Draft</div>
                )}
              </div>

              <div style={analyticsGridStyle}>
                <div style={analyticsBoxStyle}>
                  <strong style={{ color: '#333', fontSize: 24 }}>{campaign.analyticsTotals?.views || 0}</strong>
                  <span style={{ color: '#666' }}>Views</span>
                </div>
                <div style={analyticsBoxStyle}>
                  <strong style={{ color: '#333', fontSize: 24 }}>{campaign.analyticsTotals?.grinds || 0}</strong>
                  <span style={{ color: '#666' }}>Grinds</span>
                </div>
                <div style={analyticsBoxStyle}>
                  <strong style={{ color: '#333', fontSize: 24 }}>{campaign.analyticsTotals?.completedDiscoveries || 0}</strong>
                  <span style={{ color: '#666' }}>Completed Discoveries</span>
                </div>
              </div>

              <div style={totalsGridStyle}>
                {FEEDBACK_OPTIONS.map((option) => (
                  <div key={option} style={totalBoxStyle}>
                    <strong style={{ color: '#333', fontSize: 22 }}>{campaign.totals[option] || 0}</strong>
                    <span style={{ color: '#666' }}>{option}</span>
                  </div>
                ))}
              </div>

              <h3 style={{ color: '#333', fontSize: 16, margin: '18px 0 10px 0' }}>Written messages</h3>
              {campaign.feedback.filter((entry) => entry.message).length === 0 ? (
                <p style={{ color: '#666', marginBottom: 0 }}>No written messages yet.</p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {campaign.feedback.filter((entry) => entry.message).map((entry) => (
                    <article key={entry.id} style={messageStyle}>
                      <div style={messageMetaStyle}>
                        <strong>{entry.answer}</strong>
                        <span>{formatDate(entry.createdAt)}</span>
                      </div>
                      <p style={{ color: '#444', lineHeight: 1.5, margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>{entry.message}</p>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(value) {
  if (!value) return 'Saved locally';
  return new Date(value).toLocaleString();
}

function formatAudience(campaign) {
  const labels = getInterestLabels(campaign.targetInterestIds);
  if (labels.length > 0) return labels.join(', ');
  return campaign.platform ? `Legacy ${campaign.platform}` : 'Legacy campaign';
}

function getStoredCompletionRate(campaign) {
  return campaign.completionTokenRate || getCompletionTokenRate(campaign.targetInterestIds);
}

async function getSharedGrindCount(campaign, localGrindCount) {
  if (areCloudGrindsEnabled() && campaign?.source === 'cloud') {
    return getCloudGrindCount(campaign.id).catch(() => localGrindCount);
  }

  return localGrindCount;
}

const pageStyle = {
  margin: '0 auto',
  maxWidth: 1000,
  padding: '30px 16px'
};

const headerStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  justifyContent: 'space-between',
  marginBottom: 18
};

const localNoticeStyle = {
  backgroundColor: '#fff8e1',
  border: '1px solid #ffe08a',
  borderRadius: 8,
  color: '#6b5300',
  fontSize: 14,
  lineHeight: 1.4,
  marginBottom: 20,
  padding: 14
};

const campaignCardStyle = {
  backgroundColor: 'white',
  border: '1px solid #ddd',
  borderRadius: 10,
  boxShadow: '0 4px 18px rgba(0,0,0,0.08)',
  padding: 20
};

const campaignHeaderStyle = {
  alignItems: 'center',
  display: 'flex',
  gap: 16,
  justifyContent: 'space-between',
  marginBottom: 18
};

const campaignContextStyle = {
  color: '#52616f',
  display: 'grid',
  fontSize: 13,
  gap: 6,
  lineHeight: 1.35,
  marginTop: 10,
  maxWidth: 620,
  overflowWrap: 'anywhere'
};

const campaignActionsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  marginTop: 14
};

const editButtonStyle = {
  backgroundColor: '#007bff',
  border: 'none',
  borderRadius: 8,
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 'bold',
  padding: '9px 14px'
};

const deleteButtonStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #f1aeb5',
  borderRadius: 8,
  color: '#b02a37',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 'bold',
  padding: '9px 14px'
};

const thumbnailStyle = {
  aspectRatio: '16 / 9',
  backgroundColor: '#111',
  borderRadius: 8,
  flex: '0 0 min(220px, 38vw)',
  overflow: 'hidden'
};

const thumbnailPlaceholderStyle = {
  alignItems: 'center',
  aspectRatio: '16 / 9',
  backgroundColor: '#eef2f6',
  border: '1px solid #d8dee6',
  borderRadius: 8,
  color: '#52616f',
  display: 'flex',
  flex: '0 0 min(220px, 38vw)',
  fontWeight: 'bold',
  justifyContent: 'center'
};

const thumbnailImageStyle = (crop = {}) => ({
  display: 'block',
  height: '100%',
  objectFit: 'cover',
  objectPosition: `${crop.x ?? 50}% ${crop.y ?? 50}%`,
  transform: `scale(${crop.zoom ?? 1})`,
  transformOrigin: `${crop.x ?? 50}% ${crop.y ?? 50}%`,
  width: '100%'
});

const analyticsGridStyle = {
  display: 'grid',
  gap: 10,
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  marginBottom: 12
};

const analyticsBoxStyle = {
  backgroundColor: '#eef7ff',
  border: '1px solid #b6dcff',
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 14,
  textAlign: 'center'
};

const totalsGridStyle = {
  display: 'grid',
  gap: 10,
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))'
};

const totalBoxStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #ddd',
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 14,
  textAlign: 'center'
};

const messageStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #ddd',
  borderRadius: 8,
  padding: 14
};

const messageMetaStyle = {
  color: '#666',
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: 13,
  gap: 10,
  justifyContent: 'space-between'
};

const primaryButtonStyle = {
  backgroundColor: '#28a745',
  border: 'none',
  borderRadius: 8,
  color: 'white',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 'bold',
  padding: '12px 18px'
};

const noticeSuccessStyle = {
  backgroundColor: '#d4edda',
  border: '1px solid #28a745',
  borderRadius: 8,
  color: '#155724',
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 15,
  padding: 12
};

const noticeErrorStyle = {
  backgroundColor: '#fff3f3',
  border: '1px solid #f1aeb5',
  borderRadius: 8,
  color: '#842029',
  fontSize: 14,
  fontWeight: 'bold',
  marginBottom: 15,
  padding: 12
};

const emptyStyle = {
  backgroundColor: 'white',
  border: '1px solid #ddd',
  borderRadius: 10,
  padding: 30,
  textAlign: 'center'
};

const statusBadgeStyle = (status) => ({
  backgroundColor: status === 'Active' ? '#effaf1' : '#f8f9fa',
  border: `1px solid ${status === 'Active' ? '#28a745' : '#ced4da'}`,
  borderRadius: 999,
  color: status === 'Active' ? '#155724' : '#495057',
  display: 'inline-block',
  fontSize: 13,
  fontWeight: 'bold',
  padding: '6px 12px'
});

export default CampaignFeedback;
